import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
  Activity,
  ArrowLeft,
  BookOpen,
  Bot,
  Database,
  FolderKanban,
  LayoutDashboard,
  ListChecks,
  MoreHorizontal,
  Paperclip,
  Pencil,
  ScrollText,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useProject } from "@/api/queries/projects"
import { PageContainer, PageHeader } from "@/components/app/page"
import { ErrorState, PageSkeleton } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ActivityTab } from "@/features/projects/activity-tab"
import { OrchestratorStatusBadge, ProjectColorDot, ProjectStatusBadge } from "@/features/projects/badges"
import { DataTab } from "@/features/projects/data-tab"
import { FilesTab } from "@/features/projects/files-tab"
import { KnowledgeTab } from "@/features/projects/knowledge-tab"
import { OrchestratorTab } from "@/features/projects/orchestrator-tab"
import { OverviewTab } from "@/features/projects/overview-tab"
import { ProjectMenuItems } from "@/features/projects/project-actions"
import { ProjectFormDialog } from "@/features/projects/project-form-dialog"
import { RulesTab } from "@/features/projects/rules-tab"
import { TasksTab } from "@/features/projects/tasks-tab"
import { useProjectEvents } from "@/features/projects/use-project-events"

const TABS = ["overview", "tasks", "orchestrator", "knowledge", "files", "rules", "data", "activity"] as const
type ProjectTab = (typeof TABS)[number]

interface ProjectSearch {
  tab?: Exclude<ProjectTab, "overview">
  /** Open knowledge document (`folder/slug`). */
  doc?: string
}

export const Route = createFileRoute("/_app/tasks/projects/$projectId")({
  staticData: { titleKey: "tasks.projects.detailTitle" },
  validateSearch: (s: Record<string, unknown>): ProjectSearch => ({
    tab:
      typeof s.tab === "string" && s.tab !== "overview" && (TABS as readonly string[]).includes(s.tab)
        ? (s.tab as ProjectSearch["tab"])
        : undefined,
    doc: typeof s.doc === "string" && s.doc ? s.doc : undefined,
  }),
  component: ProjectPage,
})

function TabCount({ n }: { n: number }) {
  if (!n) return null
  return (
    <span className="bg-muted text-muted-foreground min-w-4 rounded px-1 text-[0.7rem] leading-4 font-medium tabular-nums">
      {n}
    </span>
  )
}

function ProjectPage() {
  const { t } = useTranslation()
  const { projectId } = Route.useParams()
  const id = Number(projectId)
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const project = useProject(id)
  const toList = () => void navigate({ to: "/tasks/projects", search: {}, replace: true })
  const { live, connection } = useProjectEvents(id, { onDeleted: toList })
  const [editing, setEditing] = useState(false)
  const tab: ProjectTab = search.tab ?? "overview"
  const setSearch = (patch: Partial<ProjectSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const back = (
    <Button variant="outline" size="sm" nativeButton={false} render={<Link to="/tasks/projects" />}>
      <ArrowLeft />
      {t("tasks.projects.backToList")}
    </Button>
  )

  if (project.isPending) return <PageSkeleton />
  if (project.isError)
    return (
      <PageContainer>
        <PageHeader title={t("tasks.projects.detailTitle")} icon={FolderKanban} actions={back} />
        <ErrorState error={project.error} onRetry={() => void project.refetch()} />
      </PageContainer>
    )

  const p = project.data
  return (
    <PageContainer width="wide">
      <PageHeader
        title={
          <span className="inline-flex min-w-0 items-center gap-2.5">
            <ProjectColorDot color={p.color} className="size-3" />
            <span className="truncate">{p.name}</span>
          </span>
        }
        documentTitle={p.name}
        description={p.description || undefined}
        badge={
          <>
            <ProjectStatusBadge status={p.status} />
            {p.orchestrator_enabled && (
              <button
                type="button"
                className="rounded-md focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]"
                onClick={() => setSearch({ tab: "orchestrator" })}
                aria-label={t("tasks.projects.tabs.orchestrator")}
              >
                <OrchestratorStatusBadge status={live.status ?? p.orchestrator_status} />
              </button>
            )}
          </>
        }
        actions={
          <>
            {back}
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil />
              {t("common.edit")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="outline" size="icon-sm" aria-label={t("tasks.projects.moreActions")} />
                }
              >
                <MoreHorizontal />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-auto min-w-48">
                <ProjectMenuItems project={p} onEdit={() => setEditing(true)} onDeleted={toList} showExport />
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <Tabs
        value={tab}
        onValueChange={(v) => setSearch({ tab: v === "overview" ? undefined : (v as ProjectSearch["tab"]) })}
        className="min-w-0 gap-5"
      >
        <div className="-mx-4 overflow-x-auto border-b px-4 sm:mx-0 sm:px-0">
          <TabsList variant="line" className="h-9">
            <TabsTrigger value="overview" className="px-2">
              <LayoutDashboard />
              {t("tasks.projects.tabs.overview")}
            </TabsTrigger>
            <TabsTrigger value="tasks" className="px-2">
              <ListChecks />
              {t("tasks.projects.tabs.tasks")}
              <TabCount n={p.open_task_count} />
            </TabsTrigger>
            <TabsTrigger value="orchestrator" className="px-2">
              <Bot />
              {t("tasks.projects.tabs.orchestrator")}
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="px-2">
              <BookOpen />
              {t("tasks.projects.tabs.knowledge")}
              <TabCount n={p.documents.length} />
            </TabsTrigger>
            <TabsTrigger value="files" className="px-2">
              <Paperclip />
              {t("tasks.projects.tabs.files")}
              <TabCount n={p.files.length} />
            </TabsTrigger>
            <TabsTrigger value="rules" className="px-2">
              <ScrollText />
              {t("tasks.projects.tabs.rules")}
              <TabCount n={p.rules.filter((r) => r.enabled).length} />
            </TabsTrigger>
            <TabsTrigger value="data" className="px-2">
              <Database />
              {t("tasks.projects.tabs.data")}
              <TabCount n={p.data.length} />
            </TabsTrigger>
            <TabsTrigger value="activity" className="px-2">
              <Activity />
              {t("tasks.projects.tabs.activity")}
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="overview">
          <OverviewTab project={p} liveStatus={live.status} onOpenTab={(tab) => setSearch({ tab })} />
        </TabsContent>
        <TabsContent value="tasks">
          <TasksTab project={p} />
        </TabsContent>
        <TabsContent value="orchestrator">
          <OrchestratorTab projectId={id} live={live} connection={connection} />
        </TabsContent>
        <TabsContent value="knowledge">
          <KnowledgeTab project={p} docRef={search.doc} onDocChange={(doc) => setSearch({ doc })} />
        </TabsContent>
        <TabsContent value="files">
          <FilesTab project={p} />
        </TabsContent>
        <TabsContent value="rules">
          <RulesTab project={p} />
        </TabsContent>
        <TabsContent value="data">
          <DataTab project={p} />
        </TabsContent>
        <TabsContent value="activity">
          <ActivityTab projectId={id} />
        </TabsContent>
      </Tabs>

      <ProjectFormDialog open={editing} onOpenChange={setEditing} project={p} />
    </PageContainer>
  )
}
